/**
 * What the UI reads off a v2 order view (OrderLayersView): one layer, whether
 * it is still coming, and the order's outcome once it is final. Pure — the
 * workspace, the generating screen and the story list share it so they agree
 * on «идёт» / «готово» / «готово частично».
 */
import type { LayerKind, LayerState } from '../contracts';
import type { OrderLayersView } from './types';

export type OrderLayerView = OrderLayersView['layers'][number];

/** A layer that will not move again by itself. */
export const FINAL_LAYER_STATES: ReadonlySet<LayerState> = new Set<LayerState>(['ready', 'partial', 'failed', 'cancelled']);

export function orderLayer(order: OrderLayersView | null | undefined, kind: LayerKind): OrderLayerView | null {
  return order?.layers.find((l) => l.kind === kind) ?? null;
}

/** The order asked for this layer (a cancelled one no longer counts). */
export function layerOrdered(order: OrderLayersView | null | undefined, kind: LayerKind): boolean {
  const l = orderLayer(order, kind);
  return !!l && l.state !== 'cancelled';
}

/** Still being made: waiting for its inputs or running. A button waiting for the parent is not. */
export function layerInFlight(layer: Pick<OrderLayerView, 'state'> | null | undefined): boolean {
  return !!layer && (layer.state === 'waiting' || layer.state === 'running');
}

/**
 * The order as a whole:
 *  - running — something is still being made;
 *  - ready — the order is final and every layer it kept is ready (a button
 *    the parent has not pressed yet does not hold it open);
 *  - partial — final, but a layer came out partial or failed («повторить»);
 *  - failed — the story itself did not come out, so nothing else could;
 *  - cancelled — the parent cancelled it.
 */
export type OrderOutcome = 'running' | 'ready' | 'partial' | 'failed' | 'cancelled';

export function orderOutcome(order: OrderLayersView | null | undefined): OrderOutcome | null {
  if (!order) return null;
  if (order.status === 'running') return 'running';
  if (order.status === 'cancelled') return 'cancelled';
  const story = orderLayer(order, 'story');
  if (story?.state === 'failed' || story?.state === 'cancelled') return 'failed';
  const kept = order.layers.filter((l) => l.state !== 'cancelled');
  if (kept.some((l) => l.state === 'partial' || l.state === 'failed')) return 'partial';
  return 'ready';
}

/** Layers a «повторить» would re-run: partial or failed ones. */
export function retryableLayers(order: OrderLayersView | null | undefined): OrderLayerView[] {
  return (order?.layers ?? []).filter((l) => l.state === 'partial' || l.state === 'failed');
}

/** Share of the kept layers that are final — a coarse progress for one bar. */
export function orderProgress(order: OrderLayersView | null | undefined): number {
  const kept = (order?.layers ?? []).filter((l) => l.state !== 'cancelled');
  if (kept.length === 0) return 0;
  const done = kept.filter((l) => FINAL_LAYER_STATES.has(l.state) || l.state === 'awaiting_user').length;
  return Math.round((done / kept.length) * 100);
}
